// src/utils/estimateEditor.js — doced-8f3a61c2 (Document editor, Stage 4: the estimate page)
//
// The estimate page's rules that aren't the save itself (that is estimateSave.js), as pure functions.
// Each one is today's estimate pop-up's rule (src/components/crm/QuoteFormModal.js), so either screen
// can open and save the other's estimates. estimateEditor.test.js lifts the pop-up's code from disk
// where it can and fails if the two ever disagree.
//
// ONE deliberate difference, from the plan (2026-10-25): dates are read on the LOCAL calendar day.
// The pop-up loads a stored date through toISOString() (UTC), so after ~8pm Eastern it can show the
// next day; the page shows the day the person actually picked.
//
// Tax (decision Q2, 2026-10-25): the line's tax choices are today's — the QuickBooks codes when the
// company is on QuickBooks, otherwise the company rate — plus "No tax (exempt)", which is the existing
// taxExempt tick. The page never writes a per-line taxSnapshot on an estimate (hard rule).

import { toJsDate, formatLocalDate } from './formatters';
import {
    DEFAULT_SELECTION_TYPE, SELECTION_TYPES, DOC_TYPES, resolveDocTerminology,
    DEPOSIT_TYPES, isValidDepositType, resolveDepositAmount, resolveDepositRequired, resolveDepositDefaults,
} from './quoteSchema';
import { ESTIMATE_DEFAULT_LINE } from './catalogPickLines';
import { estimateTermsFromCompany, termsProblem } from './documentSettings'; // doced-8f3a61c2 — Stage 5e
import { makeTaxCodeResolver, phase0Lookups, computeDocumentTotals, taxPartKind } from './totalsEngine';

// secfix-7b3e9d42 — security audit 2026-10-29 #3: once the SAVED estimate is approved (or approved and waiting for its
// deposit), firestore.rules refuse any change to its Terms — the customer signed under them, and the approval record
// keeps them. The page shows the box read-only then. Same statuses as the rule (termsWriteSafe).
export const TERMS_LOCKED_STATUSES = ['Approved', 'approved', 'Accepted', 'accepted', 'AwaitingDeposit'];
export const TERMS_LOCKED_NOTE = 'Terms are locked because the customer approved this estimate.';
export const termsLockedFor = (stored) => !!(stored && stored.id && TERMS_LOCKED_STATUSES.includes(stored.status));

// The statuses the pop-up's Status select offers.
export const ESTIMATE_STATUS_CHOICES = Object.freeze(['Draft', 'Sent', 'Approved', 'Declined']);

// ── dates ────────────────────────────────────────────────────────────────────
/** A stored date (Timestamp / Date / 'YYYY-MM-DD') → the date box's 'YYYY-MM-DD', on the LOCAL day. */
export function dateBoxValue(value) {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const d = toJsDate(value);
    return d ? formatLocalDate(d) : '';
}

// ── title ────────────────────────────────────────────────────────────────────
/**
 * The pop-up's create-only title rule: a blank title, or one still equal to a bare document word
 * ("Quote" / "Estimate"), follows the chosen document type's word; anything else is the person's.
 */
export function autoTitle(prevTitle, docTypeOrDefault) {
    const qWord = resolveDocTerminology(DOC_TYPES.QUOTE).word;
    const eWord = resolveDocTerminology(DOC_TYPES.ESTIMATE).word;
    const word = resolveDocTerminology(docTypeOrDefault).word;
    const isAuto = !prevTitle || prevTitle === qWord || prevTitle === eWord;
    return (isAuto && prevTitle !== word) ? word : prevTitle;
}

/** The document type a new estimate is made as: the person's choice, else the company default. */
export const effectiveDocType = (quoteData, companySettings) => quoteData.docType ?? companySettings?.quoteTerminologyDefault;

// ── loading ──────────────────────────────────────────────────────────────────
/**
 * The editor's starting state — the pop-up's initial quoteData, then its load effect, then (for a new
 * estimate) its title effect. editingQuote = the stored estimate (with id), a prefill (no id: from a
 * customer, a lead or a re-quote; may carry `customer`), or null for a blank new estimate.
 * deps = { today, defaultExpiry, newLine () → a blank line with its own ids, generateItemId, companySettings }
 */
export function initialQuoteData(editingQuote, deps) {
    const { today, defaultExpiry, newLine, generateItemId, companySettings } = deps;
    const base = {
        customerId: '', customerName: '', customerDetails: {},
        propertyId: null, propertyName: null, serviceAddress: null,
        billTo: null,
        title: 'Quote', description: '',
        createdAt: today, expiresAt: defaultExpiry,
        lineItems: [newLine()],
        notes: '', status: 'Draft',
        estimatedDuration: 0,
    };
    let out = base;
    if (editingQuote) {
        const initialData = { ...editingQuote };
        if (!editingQuote.id && editingQuote.customer) {
            const customer = editingQuote.customer;
            initialData.customerId = customer.id;
            initialData.customerName = customer.name;
            initialData.customerDetails = {
                name: customer.name, email: customer.email || '', phone: customer.phone || '',
                address1: customer.address1 || '', address2: customer.address2 || '', city: customer.city || '',
                state: customer.state || '', zip: customer.zip || '', country: customer.country || '',
            };
        }
        const lineItems = (initialData.lineItems && initialData.lineItems.length > 0)
            ? initialData.lineItems.map((item) => ({
                ...ESTIMATE_DEFAULT_LINE,
                ...item,
                type: item.type || 'Service',
                taxCodeId: item.taxCodeId || '',
                taxExempt: item.taxExempt || false,
                itemId: item.itemId || generateItemId(),
                selectionType: item.selectionType || DEFAULT_SELECTION_TYPE,
                parentItemId: Object.prototype.hasOwnProperty.call(item, 'parentItemId') ? item.parentItemId : null,
            }))
            : [newLine()];
        out = {
            ...base,
            ...initialData,
            lineItems,
            createdAt: dateBoxValue(initialData.createdAt) || today,
            // A saved estimate with no expiry stays blank (a plain save must not give it one).
            expiresAt: (editingQuote.id && !initialData.expiresAt) ? '' : (dateBoxValue(initialData.expiresAt) || defaultExpiry),
        };
    }
    if (!editingQuote?.id) out = { ...out, title: autoTitle(out.title, effectiveDocType(out, companySettings)) };
    // doced-8f3a61c2 — Stage 5e: a NEW estimate starts with the company's default Terms (Settings › Documents,
    // documentTerms.estimate) unless what it was opened from already carries terms. A stored estimate keeps
    // exactly what it has — no terms stays no terms (the key isn't added, so a plain save writes nothing).
    // The one deliberate difference from the pop-up's opening (estimateEditor.test O).
    if (!editingQuote?.id && typeof out.terms !== 'string') out = { ...out, terms: estimateTermsFromCompany(companySettings) };
    return out;
}

// ── deposit ──────────────────────────────────────────────────────────────────
/** The deposit controls on opening (the pop-up's seed): { enabled, type, value (string), required }. */
export function seedDeposit(editingQuote, companySettings) {
    const saved = editingQuote?.deposit;
    const savedValue = Number(saved?.value);
    const hasSaved = Number.isFinite(savedValue) && savedValue > 0;
    const required = resolveDepositRequired(editingQuote?.depositRequired, companySettings?.depositRequiredDefault);
    const isNew = !editingQuote?.id;
    const defaults = resolveDepositDefaults(companySettings?.depositDefaults);
    const useOrg = isNew && defaults.enabled && defaults.value > 0;
    return {
        type: isValidDepositType(saved?.type) ? saved.type : useOrg ? defaults.type : DEPOSIT_TYPES.PERCENTAGE,
        value: hasSaved ? String(savedValue) : useOrg ? String(defaults.value) : '',
        required,
        enabled: hasSaved || editingQuote?.depositAmountNeeded === true || (isNew && (required || useOrg)), // depdraft-3c8e5a14
    };
}

/** depdraft-3c8e5a14 — "Collect a deposit" is ON (and still editable) but no amount above zero is entered. */
export const depositAmountMissing = (deposit, depositLocked) => !depositLocked && !!deposit?.enabled && !(Number(deposit.value) > 0);

/** Approved can't be chosen: a saved, configured, REQUIRED deposit not yet settled (the rules agree). */
export function isApprovalBlockedByDeposit(editingQuote, companySettings) {
    const dep = editingQuote?.deposit;
    const configured = !!dep?.type && Number(dep?.value) > 0;
    const required = resolveDepositRequired(editingQuote?.depositRequired, companySettings?.depositRequiredDefault);
    const settled = ['paid', 'recorded', 'waived', 'refunded'].includes(editingQuote?.depositStatus);
    return configured && required && !settled;
}

/**
 * Approving with an OPTIONAL deposit marks it owing (the server's F1 heal). → { amount } when this save
 * would do that (so the page asks first), else null.
 */
export function optionalDepositOnApproval({ depositLocked, deposit, editingQuoteStatus, approvedTotal, total }) {
    if (depositLocked) return null;
    if (!deposit.enabled || deposit.required) return null;
    if (['approved', 'accepted'].includes((editingQuoteStatus || '').toLowerCase())) return null;
    const value = Number(deposit.value);
    if (!Number.isFinite(value) || value <= 0) return null;
    const approvedTotalNum = Number(approvedTotal);
    const base = (Number.isFinite(approvedTotalNum) && approvedTotalNum > 0) ? approvedTotalNum : total;
    const amount = resolveDepositAmount(deposit.type, value, base);
    if (!(amount > 0)) return null;
    return { amount };
}

// ── checks before saving ─────────────────────────────────────────────────────
/**
 * The pop-up's checks, in its order and words. → the first problem (a sentence) or null.
 * { quoteData, customers, serviceLocation, deposit, depositLocked, pendingThirdParty, sendEmail }
 * sendEmail: the pop-up's Save & Send. The page never sends from its save (Review & send does), so it leaves it out.
 */
export function findSaveProblem({ quoteData, customers, serviceLocation, deposit, depositLocked, pendingThirdParty, sendEmail = false }) {
    if (!quoteData.customerId) return 'Please select a customer.';
    const cust = (customers || []).find((c) => c.id === quoteData.customerId);
    if ((cust?.properties || []).length > 0 && serviceLocation === '') return 'This customer has multiple service locations. Please choose which one.';
    const unnamedIndex = (quoteData.lineItems || []).findIndex((item) => !(item.name || '').trim());
    if (unnamedIndex !== -1) return `Line item ${unnamedIndex + 1} needs a name before saving.`;
    const items = quoteData.lineItems || [];
    const itemById = new Map(items.filter((i) => i.itemId).map((i) => [i.itemId, i]));
    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.selectionType !== SELECTION_TYPES.INCLUDED) continue;
        if (!item.parentItemId) return `Line ${i + 1} ("${item.name || 'unnamed'}") is marked Included but has no parent line selected. Please choose a parent or change its type.`;
        const parent = itemById.get(item.parentItemId);
        if (!parent) return `Line ${i + 1} ("${item.name || 'unnamed'}") references a parent that no longer exists. Please pick a different parent or change its type.`;
        if (parent.selectionType === SELECTION_TYPES.INCLUDED) return `Line ${i + 1} ("${item.name || 'unnamed'}") is Included under another Included item. Pick a Required, Recommended, or Add-on line as the parent instead.`;
    }
    if (!depositLocked && deposit.enabled) {
        const v = Number(deposit.value);
        if (!Number.isFinite(v) || v <= 0) {
            // depdraft-3c8e5a14 — a plain Save of a Draft may keep it ON with no amount; sending may not.
            const statusNow = String(quoteData.status || '').toLowerCase();
            if (sendEmail || statusNow === 'sent') return 'Enter the deposit amount before sending.';
            if (statusNow !== 'draft') return 'Enter a deposit amount greater than zero, or turn off "Collect a deposit".';
        } else if (deposit.type === DEPOSIT_TYPES.PERCENTAGE && v > 100) return "A percentage deposit can't be more than 100%.";
    }
    if (pendingThirdParty && !quoteData.billTo) return 'Choose which customer to bill, or set Bill to back to Main customer.';
    if (typeof quoteData.terms === 'string' && termsProblem(quoteData.terms)) return termsProblem(quoteData.terms); // doced-8f3a61c2 — same 10,000 cap as Settings
    return null;
}

// ── line edits (the pop-up's handleLineItemChange side effects) ──────────────
/** Set one field on a line, with the pop-up's side effects. value is already the stored value. */
export function setLineField(line, name, value) {
    const out = { ...line, [name]: value };
    if (name === 'taxExempt' && !value) out.taxExemptionNumber = '';
    if (name === 'selectionType') {
        if (value === SELECTION_TYPES.INCLUDED) out.price = 0;
        out.parentItemId = null;
    }
    return out;
}

/** A number box's text → what the pop-up stores ('' stays '', else parseFloat), or undefined = refused (over the cap / not a number). */
export function lineNumberValue(text, max) {
    if (text === '') return '';
    const n = parseFloat(text);
    if (!(n <= max)) return undefined;
    return n;
}
export const QTY_MAX = 99999;
export const PRICE_MAX = 999999.99;

/** Move a line (drag to reorder / Move up / Move down). Out-of-range moves return the same list. */
export function moveLine(lines, from, to) {
    const list = Array.isArray(lines) ? [...lines] : [];
    if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    return list;
}

/** Lines another line could be Included in (the pop-up's parent list): not itself, has an itemId, not Included. */
export const parentChoices = (lines, index) => (lines || []).filter((other, i) => i !== index && other.itemId && other.selectionType !== SELECTION_TYPES.INCLUDED);

// ── tax ──────────────────────────────────────────────────────────────────────
export const EXEMPT_CHOICE = '__exempt';

/**
 * The QuickBooks codes in the engine's shape. A code the server answered without parts but with a
 * rate (an older answer) becomes one part, so it is never silently treated as 0%.
 */
export function engineCodesFrom(taxOptions) {
    return (Array.isArray(taxOptions) ? taxOptions : []).map((o) => {
        const parts = Array.isArray(o.components) ? o.components : [];
        const rate = Number(o.Rate);
        return {
            Id: String(o.Id),
            Name: o.Name || '',
            components: parts.length ? parts : (Number.isFinite(rate) && rate !== 0 ? [{ key: `qbcode:${o.Id}`, name: o.Name || `Code ${o.Id}`, rate, kind: taxPartKind(o.Name) }] : []),
        };
    });
}

/**
 * The totals engine's context for this company, the way the server's totals shadow builds it: on
 * QuickBooks → its codes (null while they haven't loaded, so a coded line is "can't work out", never
 * guessed) and its default code; otherwise the company rate.
 * { companySettings, taxOptions, taxLoaded }
 */
export function estimateTaxContext({ companySettings, taxOptions, taxLoaded }) {
    const qbConnected = companySettings?.quickbooksConnected === true;
    const qbTaxCodes = !qbConnected ? [] : (taxLoaded ? engineCodesFrom(taxOptions) : null);
    return {
        resolveTaxCode: makeTaxCodeResolver(phase0Lookups({
            qbTaxCodes,
            qbDefaultTaxCodeId: companySettings?.quickbooksSettings?.defaultTaxCodeId || '',
            legacyRate: Number(companySettings?.taxRate) || 0,
        })),
    };
}

/** The estimate's totals from the engine (per cent, once per tax part). */
export const estimateTotals = (lineItems, ctx) => computeDocumentTotals({ lineItems }, ctx);

/** The line's Tax dropdown value: EXEMPT_CHOICE, a QuickBooks code id, or '' (the default). */
export const lineTaxValue = (line) => (line.taxExempt ? EXEMPT_CHOICE : (line.taxCodeId || ''));

/** Apply a Tax dropdown choice. Exempt keeps the code (the pop-up's tick does too); a code clears exempt. */
export function applyTaxChoice(line, value) {
    if (value === EXEMPT_CHOICE) return { ...line, taxExempt: true };
    return { ...line, taxExempt: false, taxExemptionNumber: '', taxCodeId: value };
}

/**
 * The Tax dropdown's choices for one line: [{ value, label, title }]. Labels are kept short so they fit
 * the line's Tax column (round 1 fix); `title` (the tooltip) says it in full.
 * '' = the default: the company's QuickBooks default code, else the company rate.
 */
export function taxChoicesFor(line, { companySettings, taxOptions, taxLoaded }) {
    const qbConnected = companySettings?.quickbooksConnected === true;
    const rate = Number(companySettings?.taxRate) || 0;
    const codes = qbConnected && taxLoaded && Array.isArray(taxOptions) ? taxOptions : [];
    const defaultId = qbConnected ? (companySettings?.quickbooksSettings?.defaultTaxCodeId || '') : '';
    const def = defaultId ? codes.find((c) => String(c.Id) === String(defaultId)) : null;
    const out = [def
        ? { value: '', label: `Default · ${def.Name}`, title: `Your QuickBooks default tax code (${def.Name})` }
        : (qbConnected && defaultId
            ? { value: '', label: 'Default code', title: 'Your QuickBooks default tax code' }
            : { value: '', label: `Company ${rate}%`, title: `Your company tax rate (${rate}%)` })];
    for (const c of codes) out.push({ value: String(c.Id), label: c.Name || `Code ${c.Id}`, title: c.Name || `QuickBooks code ${c.Id}` });
    // A stored code that isn't in the list (QuickBooks no longer has it, or the list didn't load) is
    // still shown, so the box never claims a different tax than the line holds.
    if (line.taxCodeId && !out.some((o) => o.value === String(line.taxCodeId))) {
        out.push({ value: String(line.taxCodeId), label: `Code ${line.taxCodeId}`, title: taxLoaded ? `QuickBooks code ${line.taxCodeId} — QuickBooks no longer has it` : `QuickBooks code ${line.taxCodeId}` });
    }
    out.push({ value: EXEMPT_CHOICE, label: 'No tax', title: 'No tax (exempt)' });
    return out;
}

/** One tax row's label: the part's name, with its rate when the name doesn't already say it. */
export const taxPartLabel = (p) => (/%/.test(p.name || '') ? p.name : `${p.name || 'Tax'} (${Number(p.rate)}%)`);

// ── /estimates/:ref ──────────────────────────────────────────────────────────
/**
 * What an address points at, from the two lookups (number matches, and the document with that id).
 * Numbers aren't unique (legacy timestamp numbers, imports), so two can match.
 * → { kind: 'none' | 'one' | 'many', quotes }
 */
export function resolveRefMatches(byNumber, byId) {
    const seen = new Map();
    for (const q of [...(byNumber || []), ...(byId ? [byId] : [])]) if (q && q.id && !seen.has(q.id)) seen.set(q.id, q);
    const quotes = [...seen.values()];
    return { kind: quotes.length === 0 ? 'none' : quotes.length === 1 ? 'one' : 'many', quotes };
}

/** The address segment for an estimate: its number when it has one, else its id. */
export const estimateRef = (quote) => (quote?.quoteNumber ? String(quote.quoteNumber) : (quote?.id || ''));
export const estimatePath = (quote) => `/estimates/${encodeURIComponent(estimateRef(quote))}`;
